// src/features/invoice/ServiceInvoiceModal.jsx
import React, { useEffect, useState } from "react";
import * as QR from "qrcode";
import { isLineChecklistEligible } from "../checklist/utils";
import { varekodeSifre, varekodeTekst } from "../../tilbud/varekode";

// Betalings-QR på fakturaen (se "Utfør betaling", src/pages/BetalService.jsx).
// Skjult inntil kortterminalen er på plass — sett til true for å vise den.
const VIS_BETAL_QR = false;

/**
 * Props:
 * - open, onClose
 * - svc, customer, produkt
 * - serviceLines, partLines, totals, workDesc, checklistSummary
 * - fmtNOK, fmtDT, fmtDate, fmtMin
 */
export default function ServiceInvoiceModal(props) {
  const {
    open,
    onClose,
    svc,
    customer,
    produkt,
    serviceLines = [],
    partLines = [],
    butikkvarer = null,

    workDesc = "",
    checklistSummary = [],
    checklistComplete = false,
    fmtNOK,
    fmtDT,
    fmtDate,
    fmtMin,
  } = props;

  // Egen lokal state for QR-koder (flyttet ut av WorkshopDetails)
  const [qrBusy, setQrBusy] = useState(false); // ✅ riktig bruk
  const [partQRCodes, setPartQRCodes] = useState({}); // { index: dataURL }
  // QR til "Utfør betaling" (/betal/<serviceRef>). Inneholder bare
  // referansenummeret — ingen token — så lappen gir ingen tilgang hvis den
  // blir mistet eller fotografert. Uinnloggede får en side uten kundedata.
  const [betalQR, setBetalQR] = useState(null);

  // 👇 ny state – av som standard
  const [pageBreakBeforeChecklist, setPageBreakBeforeChecklist] = useState(true);

  const [paidAtCounter, setPaidAtCounter] = useState(
    svc?.invoice?.paidAtCounter || false
  );

  const [etterjustering, setEtterjustering] = useState(
    svc?.invoice?.etterjustering || false
  );

  const [svcLines, setSvcLines] = useState([]);
  const [prtLines, setPrtLines] = useState([]);

  // Sync når modalen åpnes / når props endres
  useEffect(() => {
    if (!open) return;

    setSvcLines((serviceLines || []).map(l => ({ ...l })));
    setPrtLines((partLines || []).map(p => ({ ...p })));
    setEtterjustering(svc?.invoice?.etterjustering || false);
  }, [open, serviceLines, partLines, svc?.invoice?.etterjustering]);

  // Reklamasjonsarbeid avledes automatisk fra reklamasjon-flagget på arbeidslinjer
  const warranty = svcLines.some(l => !!l.reklamasjon);
  const partsWarranty = prtLines.some(p => !!p.isWarranty);
  // Gratisarbeid gir samme "strøket pris"-visning som reklamasjon, og
  // subtotalen for arbeid skal derfor skjules på samme måte for begge.
  const hasFreeArbeidLine = svcLines.some(l => !!l.reklamasjon || !!l.gratisarbeid);

  // Auto-huk av etterjustering når en servicelinje med slug 'etterjustering' finnes
  useEffect(() => {
    if (!open) return;
    const hasEtterjusteringLine = (serviceLines || []).some(
      l => String(l.slug || "").toLowerCase() === "etterjustering"
    );
    if (hasEtterjusteringLine && !svc?.invoice?.etterjustering) {
      setEtterjustering(true);
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/workshop-services/${svc._id}/invoice-settings`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ etterjustering: true }),
        }
      ).catch(err => console.error("Kunne ikke lagre etterjustering:", err));
    }
  }, [open, serviceLines, svc?.invoice?.etterjustering, svc?._id]);


  // Slå av/på print-context CSS på body når modalen er åpen
  useEffect(() => {
    if (open) document.body.classList.add("printing-invoice");
    else document.body.classList.remove("printing-invoice");
    return () => document.body.classList.remove("printing-invoice");
  }, [open]);

  // Generer QR når modalen åpnes / deler endres
  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      try {
        setQrBusy(true);
        const map = {};

        await Promise.all(
          (prtLines || []).map(async (p, idx) => {
            const eanDigits = String(p?.ean || "").replace(/\D/g, "");
            const hasValidEan =
              eanDigits.length >= 11 && eanDigits.length <= 14;

            if (!hasValidEan) {
              // Ikke generer QR for denne linjen
              map[idx] = null;
              return;
            }

            const text = eanDigits; // QR-tekst = ren EAN
            try {
              map[idx] = await QR.toDataURL(text, { margin: 1, scale: 4 });
            } catch {
              map[idx] = null;
            }
          })
        );

        if (alive) setPartQRCodes(map);
      } finally {
        if (alive) setQrBusy(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, prtLines]);

  // QR med EAN på varene fra butikken, som på delene, så kassa kan skanne dem.
  const [vareQR, setVareQR] = useState([]);
  useEffect(() => {
    if (!open || !Array.isArray(butikkvarer?.linjer)) { setVareQR([]); return undefined; }
    let alive = true;
    Promise.all(butikkvarer.linjer.map(async (l) => {
      const ean = varekodeSifre(l?.ean); // EAN eller PLU
      if (!ean) return null;
      try { return await QR.toDataURL(ean, { margin: 1, scale: 4 }); } catch { return null; }
    })).then((liste) => { if (alive) setVareQR(liste); });
    return () => { alive = false; };
  }, [open, butikkvarer]);

  // Generer betalings-QR når modalen åpnes
  useEffect(() => {
    if (!VIS_BETAL_QR || !open || !svc?.serviceRef) {
      setBetalQR(null);
      return;
    }
    let alive = true;
    const url = `${window.location.origin}/betal/${encodeURIComponent(svc.serviceRef)}`;
    QR.toDataURL(url, { margin: 1, scale: 4 })
      .then((dataUrl) => { if (alive) setBetalQR(dataUrl); })
      .catch(() => { if (alive) setBetalQR(null); });
    return () => {
      alive = false;
    };
  }, [open, svc?.serviceRef]);

  if (!open) return null;

  // Rabatt?
  const hasAnyDiscount = prtLines.some((p) => {
    const discountKr = Number(p.discount);
    return Number.isFinite(discountKr) && discountKr > 0;
  });

  // Hjelpefunksjon: linjepris for en del
  const partLineTotal = (p) => {
    const price = Number(p.price) || 0;
    const qty = Math.max(1, Number(p.qty) || 1);
    const subtotal = price * qty;
    let discountKr = Math.max(0, Number(p.discount) || 0);
    const discountPercent = Number(p.discountPercent) || 0;
    if (!discountKr && discountPercent && subtotal > 0) {
      discountKr = Math.round((subtotal * discountPercent) / 100);
    }
    return Math.max(0, subtotal - discountKr);
  };

  // Brutto (alle linjer, ingen fradrag)
  const arbeidGross = svcLines.reduce((sum, l) => sum + (Number(l.pris) || 0), 0);
  const delerGross = prtLines.reduce((sum, p) => sum + partLineTotal(p), 0);
  const grossTotal = arbeidGross + delerGross;

  // Reklamasjonsandel (linjer merket med reklamasjon ved registrering)
  const warrantyAmount =
    svcLines.reduce((sum, l) => l.reklamasjon ? sum + (Number(l.pris) || 0) : sum, 0) +
    prtLines.reduce((sum, p) => p.isWarranty ? sum + partLineTotal(p) : sum, 0);

  // Gratisarbeidsandel (linjer merket som gratisarbeid)
  const gratisarbeidAmount = svcLines.reduce(
    (sum, l) => (l.gratisarbeid ? sum + (Number(l.pris) || 0) : sum),
    0
  );
  const hasGratisarbeidLines = svcLines.some(l => !!l.gratisarbeid);

  // Etterjusteringsandel
  const etterjusteringAmount =
    svcLines.reduce((sum, l) => l.isEtterjustering ? sum + (Number(l.pris) || 0) : sum, 0) +
    prtLines.reduce((sum, p) => p.isEtterjustering ? sum + partLineTotal(p) : sum, 0);

  // Beregn arbeid som skal betales
  const arbeidToPay = svcLines.reduce((sum, l) => {
    if (l.reklamasjon) return sum;
    if (l.gratisarbeid) return sum;
    if (etterjustering && l.isEtterjustering) return sum;
    return sum + (Number(l.pris) || 0);
  }, 0);

  // Beregn deler som skal betales
  const delerToPay = prtLines.reduce((sum, p) => {
    if (p.isWarranty) return sum;
    if (etterjustering && p.isEtterjustering) return sum;
    return sum + partLineTotal(p);
  }, 0);

  const totalToPayRaw = arbeidToPay + delerToPay;

  // Hvis betalt i butikk → 0
  const totalToPay = paidAtCounter ? 0 : totalToPayRaw;

  // Varer fra butikken (lagt på servicen, eller fra ordre/skiplukk/pristilbud koblet til den, src/lib/butikkvarer.js):
  // vises samlet med servicen, men er ikke del av servicens beløp eller verkstedstatistikken.
  const varer = Array.isArray(butikkvarer?.linjer) ? butikkvarer.linjer : [];
  const harVarer = varer.length > 0;
  const varerSum = varer.reduce((s, l) => s + (Number(l.totalprisEtterRabatt) || 0), 0);
  const varerToPay = paidAtCounter ? 0 : varerSum;
  // Med varer lagt rett på servicen (ServiceButikkvarer) er ikke alt fra ordren, så ingen kilde.
  const varerKilde = butikkvarer?.egneLinjer ? "" : butikkvarer?.ordre
    ? `${butikkvarer.ordre.type === "skiplukk" ? "Skiplukk" : "Ordre"} #${butikkvarer.ordre.ordreid}`
    : butikkvarer?.tilbud ? `Pristilbud #${butikkvarer.tilbud.offerRef}` : "";

  const hasWarrantyLines =
    svcLines.some(l => l.reklamasjon) ||
    prtLines.some(p => p.isWarranty);

  const hasEtterjusteringLines =
    svcLines.some(l => l.isEtterjustering) ||
    prtLines.some(p => p.isEtterjustering);


  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const saveInvoiceSettings = async (updates) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/invoice-settings`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        console.error("❌ Backend svarte feil:", text);
        return;
      }

      const data = await response.json();
      console.log("✅ Invoice lagret:", data);
    } catch (err) {
      console.error("❌ Kunne ikke lagre invoice settings", err);
    }
  };

  return (
    <div
      id="invoice-print-root"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg max-w-[96vw] max-h-[95vh] overflow-auto print:p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <style>{STYLE}</style>

        {/* Ikke-printbar header */}
        <div className="no-print flex items-center justify-between mb-3 p-6 pb-0">
          <div>
            <h4 className="text-lg font-semibold">Servicefaktura (for utskrift)</h4>
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={pageBreakBeforeChecklist}
                onChange={(e) => setPageBreakBeforeChecklist(e.target.checked)}
              />
              <span>Start sjekkliste på ny side ved utskrift</span>
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={paidAtCounter}
                onChange={(e) => {
                  const value = e.target.checked;
                  setPaidAtCounter(value);
                  saveInvoiceSettings({ paidAtCounter: value });
                }} />
              <span>Kunden har betalt i butikk</span>
            </label>

            <label className="mt-1 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={etterjustering}
                onChange={async (e) => {
                  const value = e.target.checked;
                  const svcSnapshot = svcLines;

                  setEtterjustering(value);
                  setSvcLines(svcSnapshot.map((l) => ({ ...l, isEtterjustering: value })));

                  await saveInvoiceSettings({ paidAtCounter, etterjustering: value });

                  try {
                    await Promise.all(
                      svcSnapshot.map((_, i) =>
                        fetch(`${API_BASE_URL}/workshop-services/${svc._id}/line-etterjustering`, {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ type: "service", index: i, value }),
                        })
                      )
                    );
                  } catch (err) {
                    console.error("Kunne ikke lagre etterjustering på alle linjer", err);
                  }
                }}
              />
              <span>Etterjustering / førstegangsservice</span>
            </label>


            {qrBusy && (
              <p className="mt-1 text-xs text-gray-500">
                Genererer QR-koder til deler… vent gjerne med utskrift.
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              disabled={qrBusy}
              className={`px-3 py-1 rounded text-white ${qrBusy
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
                }`}
            >
              {qrBusy ? "Venter på QR…" : "Skriv ut"}
            </button>
            <button onClick={onClose} className="px-3 py-1 rounded border">
              Lukk
            </button>
          </div>
        </div>

        {/* Papiret */}
        <div
          id="invoice-paper"
          className="bg-white w-[200mm] md:w-[205mm] lg:w-[210mm] p-6 md:p-8 my-6 mx-auto"
        >
          <div id="invoice-print-area">
            {/* Topp */}
            <div
              className="row"
              style={{ alignItems: "flex-start", marginBottom: 12 }}
            >
              <div className="col">
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  Servicefaktura
                </div>
                {paidAtCounter && (
                  <div style={{
                    color: "#059669",
                    fontWeight: 800,
                    fontSize: 16,
                    marginTop: 4
                  }}>
                    ✓ BETALT
                  </div>
                )}

                {warranty && (
                  <div style={{
                    color: "#2563eb",
                    fontWeight: 800,
                    fontSize: 16,
                    marginTop: 4
                  }}>
                    ✓ REKLAMASJONSARBEID
                  </div>
                )}

                {etterjustering && (
                  <div style={{
                    color: "#059669",
                    fontWeight: 800,
                    fontSize: 16,
                    marginTop: 4
                  }}>
                    ✓ ETTERJUSTERING / GRATIS FØRSTEGANGSSERVICE
                  </div>
                )}

                <div className="muted">
                  Utskriftsdato: {fmtDT(new Date())}
                </div>
              </div>
              <div className="col" style={{ textAlign: "right" }}>
                <div className="service-ref-box" aria-label="Servicenummer">
                  <div className="service-ref-label">Service</div>
                  <div className="service-ref-value">
                    {svc?.serviceRef ? `#${svc.serviceRef}` : "—"}
                  </div>
                </div>

                {betalQR && !paidAtCounter && svc?.vipps?.payment?.status !== "betalt" && (
                  <div className="betal-qr">
                    <img src={betalQR} alt={`QR-kode for betaling av service ${svc.serviceRef}`} />
                    <div className="betal-qr-label">Skannes i kassen ved betaling</div>
                  </div>
                )}

                <div className="muted no-print">
                  Status: {svc?.status || "—"}
                </div>
                {produkt?.butikk ? (
                  <div className="muted">Butikk: {produkt.butikk}</div>
                ) : null}
              </div>
            </div>
            <div className="h-line" />

            {/* Kunde & produkt */}
            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col">
                <div className="section-title">Kunde</div>
                <div>
                  <strong>
                    {customer?.firstName} {customer?.lastName}
                  </strong>
                </div>
                <div className="muted">
                  {customer?.phoneNumber || "—"}
                </div>
                <div className="muted">{customer?.email || "—"}</div>
              </div>
              <div className="col">
                <div className="section-title">Produkt</div>
                <div>
                  <strong>
                    {produkt?.merke}
                    {produkt?.type ? ` – ${produkt.type}` : ""}
                  </strong>
                </div>
                {produkt?.farge && (
                  <div className="muted">Farge: {produkt.farge}</div>
                )}
                {produkt?.leveringsDato && (
                  <div className="muted">
                    Leveringsdato: {fmtDate(produkt.leveringsDato)}
                  </div>
                )}
                {produkt?.forventetFerdigDato && (
                  <div className="muted no-print">
                    Forventet ferdig: {fmtDate(produkt.forventetFerdigDato)}
                  </div>
                )}
              </div>
            </div>

            {/* Arbeid */}
            <div className="section-title">Arbeid</div>
            <table className="tbl" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Beskrivelse</th>
                  <th className="right" style={{ width: "22%" }}>
                    Pris
                  </th>
                </tr>
              </thead>

              <tbody>
                {serviceLines.length ? (
                  svcLines.map((l, i) => (
                    <tr key={l._id || i}>
                      <td>
                        <div>
                          <strong>{l.navn || "Tjeneste"}</strong>
                        </div>

                        {l.kommentar ? (
                          <div className="muted">{l.kommentar}</div>
                        ) : null}

                        {isLineChecklistEligible(l) && checklistComplete ? (
                          <div className="muted">Se sjekkliste på neste side/bakside for detaljer</div>
                        ) : null}

                        {l.tidsbruk ? (
                          <div className="muted no-print">
                            {fmtMin(l.tidsbruk)}
                          </div>
                        ) : null}
                      </td>

                      <td className="right">
                        {l.reklamasjon ? (
                          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                              {fmtNOK(l.pris)}
                            </span>
                            <span style={{ color: "#2563eb", fontWeight: 700, fontSize: "0.8em", background: "#eff6ff", borderRadius: 4, padding: "1px 5px" }}>
                              Reklamasjon
                            </span>
                          </span>
                        ) : l.gratisarbeid ? (
                          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                              {fmtNOK(l.pris)}
                            </span>
                            <span style={{ color: "#7c3aed", fontWeight: 700, fontSize: "0.8em", background: "#f5f3ff", borderRadius: 4, padding: "1px 5px" }}>
                              Gratisarbeid
                            </span>
                          </span>
                        ) : l.isEtterjustering ? (
                          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                              {fmtNOK(l.pris)}
                            </span>
                            <span style={{ color: "#059669", fontWeight: 700, fontSize: "0.8em", background: "#ecfdf5", borderRadius: 4, padding: "1px 5px" }}>
                              Etterjustering
                            </span>
                          </span>
                        ) : (
                          fmtNOK(l.pris)
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="muted">
                      Ingen tjenester
                    </td>
                  </tr>
                )}
              </tbody>

              {!hasFreeArbeidLine && (
                <tfoot>
                  <tr>
                    <td className="right">
                      <strong>Sum arbeid</strong>
                    </td>
                    <td className="right">
                      <strong>{fmtNOK(arbeidGross)}</strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>

            {/* Deler */}
            <div className="section-title">Deler</div>
            <table className="tbl" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Del</th>
                  <th style={{ width: "22%" }}>QR</th>
                  <th className="right" style={{ width: "10%" }}>
                    Antall
                  </th>
                  <th className="right" style={{ width: "14%" }}>
                    Enhetspris
                  </th>
                  {hasAnyDiscount && (
                    <th className="right" style={{ width: "10%" }}>
                      Rabatt
                    </th>
                  )}

                  <th className="right" style={{ width: "14%" }}>
                    Linje
                  </th>
                </tr>
              </thead>

              <tbody>
                {partLines.length ? (
                  prtLines.map((p, i) => {
                    const price = Number(p.price) || 0;
                    const qty = Math.max(1, Number(p.qty) || 1);
                    const subtotal = price * qty;

                    let discountKr = Math.max(0, Number(p.discount) || 0);
                    let discountPercent =
                      p.discountPercent != null
                        ? Number(p.discountPercent) || 0
                        : 0;

                    if (!discountPercent && subtotal > 0 && discountKr > 0) {
                      discountPercent =
                        Math.round((discountKr / subtotal) * 1000) / 10;
                    }

                    const lineTotal = Math.max(0, subtotal - discountKr);

                    const rabattText =
                      discountPercent > 0
                        ? `${discountPercent.toLocaleString("no-NO", {
                          maximumFractionDigits: 1,
                        })} %`
                        : "—";

                    return (
                      <tr key={`${i}-${p.ean || p.product || "part"}`}>
                        <td>
                          <div>
                            <strong>{p.product || "Del"}</strong>
                            {p.brand ? (
                              <span className="muted"> ({p.brand})</span>
                            ) : null}
                          </div>

                          <div className="muted">
                            EAN: {p.ean || "—"}
                          </div>
                        </td>

                        <td>
                          {partQRCodes[i] ? (
                            <img
                              src={partQRCodes[i]}
                              alt="QR"
                              style={{ width: 56, height: 56 }}
                            />
                          ) : null}
                        </td>

                        <td className="right">{p.qty || 1}</td>
                        <td className="right">{fmtNOK(p.price)}</td>

                        {hasAnyDiscount && (
                          <td className="right">
                            {rabattText !== "—" ? rabattText : ""}
                          </td>
                        )}

                        <td className="right">
                          {p.isWarranty ? (
                            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                              <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                                {fmtNOK(lineTotal)}
                              </span>
                              <span style={{ color: "#2563eb", fontWeight: 700, fontSize: "0.8em", background: "#eff6ff", borderRadius: 4, padding: "1px 5px" }}>
                                Reklamasjon
                              </span>
                            </span>
                          ) : p.isEtterjustering ? (
                            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                              <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
                                {fmtNOK(lineTotal)}
                              </span>
                              <span style={{ color: "#059669", fontWeight: 700, fontSize: "0.8em", background: "#ecfdf5", borderRadius: 4, padding: "1px 5px" }}>
                                Etterjustering
                              </span>
                            </span>
                          ) : (
                            fmtNOK(lineTotal)
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={hasAnyDiscount ? 6 : 5} className="muted">
                      Ingen deler
                    </td>
                  </tr>
                )}
              </tbody>

              {!partsWarranty && (
                <tfoot>
                  <tr>
                    <td colSpan={hasAnyDiscount ? 5 : 4} className="right">
                      <strong>Sum deler</strong>
                    </td>
                    <td className="right">
                      <strong>{fmtNOK(delerGross)}</strong>
                    </td>
                  </tr>
                </tfoot>
              )}

            </table>

            {/* Varer fra butikken (betales i kassa; ikke del av servicen) */}
            {harVarer && (
              <>
                <div className="section-title">Varer fra butikken{varerKilde ? ` (${varerKilde})` : ""}</div>
                <table className="tbl" style={{ marginBottom: 12 }}>
                  <thead>
                    <tr>
                      <th>Vare</th>
                      <th style={{ width: "10%" }}>QR</th>
                      <th className="right" style={{ width: "10%" }}>Antall</th>
                      <th className="right" style={{ width: "18%" }}>Pris</th>
                      <th className="right" style={{ width: "16%" }}>Rabatt</th>
                      <th className="right" style={{ width: "18%" }}>Sum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {varer.map((l, i) => (
                      <tr key={i}>
                        <td>
                          {l.artikkel || "Vare"}
                          {l.ean ? <div className="muted">{varekodeTekst(l.ean)}</div> : null}
                        </td>
                        <td>{vareQR[i] ? <img src={vareQR[i]} alt="QR" style={{ width: 56, height: 56 }} /> : null}</td>
                        <td className="right">{Number(l.antall) || 1}</td>
                        <td className="right">{fmtNOK(Number(l.pris) || 0)}</td>
                        <td className="right">{Number(l.totalRabattKr) > 0 ? fmtNOK(-Number(l.totalRabattKr)) : ""}</td>
                        <td className="right">{fmtNOK(Number(l.totalprisEtterRabatt) || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="right"><strong>Sum varer</strong></td>
                      <td className="right"><strong>{fmtNOK(varerSum)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </>
            )}

            {/* Arbeidsbeskrivelse */}
            <div className="section-title">Arbeidsbeskrivelse</div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 8,
                minHeight: 56,
              }}
            >
              {(workDesc || "").trim() || (
                <span className="muted">Ingen beskrivelse</span>
              )}
            </div>

            {/* Total */}
            <div className="row" style={{ marginTop: 16 }}>
              <div className="col" />
              <div
                className="col"
                style={{ maxWidth: 320, marginLeft: "auto" }}
              >
                <table className="tbl">
                  <tbody>
                    {/* Totalpris skjules når reklamasjon eller gratisarbeid trekker
                        fra deler av summen – da vil «Totalpris» lett misforstås som
                        beløpet kunden skal betale. Andelene under (struket over) og
                        «Å betale» forklarer da regnestykket i stedet. */}
                    {warrantyAmount <= 0 && gratisarbeidAmount <= 0 && (
                      <tr>
                        <td className="right"><strong>{harVarer ? "Totalpris service" : "Totalpris"}</strong></td>
                        <td className="right" style={{ width: "40%" }}>
                          <strong>{fmtNOK(grossTotal)}</strong>
                        </td>
                      </tr>
                    )}

                    {hasWarrantyLines && warrantyAmount > 0 && (
                      <tr>
                        <td className="right">Reklamasjonsandel</td>
                        <td className="right text-blue-600">
                          <span style={{ textDecoration: "line-through" }}>{fmtNOK(warrantyAmount)}</span>
                        </td>
                      </tr>
                    )}

                    {hasGratisarbeidLines && gratisarbeidAmount > 0 && (
                      <tr>
                        <td className="right">Gratisarbeidsandel</td>
                        <td className="right" style={{ color: "#7c3aed" }}>
                          <span style={{ textDecoration: "line-through" }}>{fmtNOK(gratisarbeidAmount)}</span>
                        </td>
                      </tr>
                    )}

                    {hasEtterjusteringLines && etterjusteringAmount > 0 && (
                      <tr>
                        <td className="right">Etterjustering</td>
                        <td className="right text-emerald-600">
                          {etterjustering
                            ? <span style={{ textDecoration: "line-through" }}>{fmtNOK(etterjusteringAmount)}</span>
                            : fmtNOK(etterjusteringAmount)
                          }
                        </td>
                      </tr>
                    )}

                    {paidAtCounter && (
                      <tr>
                        <td className="right">Betalt</td>
                        <td className="right text-emerald-600">
                          {fmtNOK(totalToPayRaw)}
                        </td>
                      </tr>
                    )}

                    {harVarer && (
                      <>
                        <tr>
                          <td className="right">Service</td>
                          <td className="right">{fmtNOK(totalToPay)}</td>
                        </tr>
                        <tr>
                          <td className="right">Varer fra butikken</td>
                          <td className="right">{fmtNOK(varerToPay)}</td>
                        </tr>
                      </>
                    )}

                    <tr>
                      <td className="right"><strong>Å betale</strong></td>
                      <td className="right">
                        <strong>{fmtNOK(totalToPay + varerToPay)}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="muted" style={{ marginTop: 6 }}>
                  Denne utskriften er til informasjon/kasse – ikke en
                  betalingsfaktura.
                </div>
              </div>
            </div>

            {/* Notis om neste side */}
            {pageBreakBeforeChecklist &&
              Array.isArray(checklistSummary) &&
              checklistSummary.length > 0 && (
                <div style={{
                  marginTop: 20,
                  borderTop: "1px dashed #d1d5db",
                  paddingTop: 10,
                  color: "#374151",
                  fontSize: 11,
                  fontStyle: "italic",
                }}>
                  → Se tilstandssjekk og beskrivelse av utført arbeid på neste side.
                </div>
              )}

            {/* Sjekkliste-kompakt */}
            {Array.isArray(checklistSummary) &&
              checklistSummary.length > 0 && (
                <>
                  {pageBreakBeforeChecklist && (
                    <div className="print-break-before" />
                  )}
                  <div className="h-line" />
                  <div
                    className="section-title"
                    style={{ marginTop: 8 }}
                  >
                    Sjekkliste
                  </div>
                  <div className="checklist-compact print-avoid-break">
                    <table
                      className="tbl tbl--compact"
                      style={{ marginBottom: 8 }}
                    >
                      <thead>
                        <tr>
                          <th>Punkt</th>
                          <th style={{ width: "22%" }}>Arbeid</th>
                          <th>Kommentar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistSummary.map((row) => {
                          const supportsDeviation =
                            row.canDeviation !== false;
                          const hasDeviation =
                            supportsDeviation && !!row.deviation;

                          return (
                            <tr key={row.slug}>
                              <td>{row.title}</td>
                              <td>
                                {row.ok && (
                                  <span
                                    className="badge--ok"
                                    style={{ marginRight: 4 }}
                                  >
                                    OK
                                  </span>
                                )}

                                {!row.ok &&
                                  row.done &&
                                  !hasDeviation && (
                                    <span
                                      className="badge--done"
                                      style={{ marginRight: 4 }}
                                    >
                                      Utført
                                    </span>
                                  )}

                                {hasDeviation && (
                                  <span className="badge--warn">
                                    {row.done
                                      ? "Avvik utbedret"
                                      : "Avvik utbedres ikke"}
                                  </span>
                                )}
                              </td>
                              <td className="muted">
                                {row.comment || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

const STYLE = `
@media print {
  @page { size: A4; margin: 12mm; }
  .printing-invoice body > *:not(.app-screen) { display: none !important; }
  .printing-invoice .app-screen > *:not(#invoice-print-root) { display: none !important; }
  .print-break-before { display:block!important; break-before:page!important; page-break-before:always!important; height:0; }
  .print-avoid-break { break-inside:avoid; page-break-inside:avoid; }
  .printing-invoice .app-screen { background:transparent!important; box-shadow:none!important; border:0!important; border-radius:0!important; padding:0!important; margin:0!important; }
  .printing-invoice #invoice-print-root { display:block!important; position:static!important; inset:auto!important; margin:0!important; padding:0!important; background:transparent!important; z-index:auto!important; }
  .printing-invoice #invoice-print-root > div { position:static!important; box-shadow:none!important; background:transparent!important; max-width:none!important; max-height:none!important; overflow:visible!important; padding:0!important; margin:0!important; }
  .no-print { display:none!important; }
  #invoice-paper {
    width: 200mm !important;
    margin: 0 auto !important;
    padding: 0 4mm !important;
    box-sizing: border-box !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: none !important;
  }
  nav, #stickyHeader { display:none!important; }
  .tbl { page-break-inside: avoid; }
  #invoice-print-area { min-height:auto!important; }
  html, body { margin:0!important; padding:0!important; }
}
#invoice-print-area { width:100%; min-height:297mm; box-sizing:border-box; font-size:12px; }
.row { display:flex; gap:12px; }
.col { flex:1; }
.tbl { width:100%; border-collapse:collapse; }
.tbl th, .tbl td { border-bottom:1px solid #e5e7eb; padding:6px 4px; text-align:left; }
.tbl th { background:#f8fafc; font-weight:600; }
.right { text-align:right; }
.muted { color:#6b7280; }
.section-title { font-weight:600; margin:16px 0 8px; font-size:14px; }
.h-line { height:1px; background:#e5e7eb; margin:6px 0 12px; }

/* Tydelig servicenummer */
.service-ref-box{
  display:inline-block;
  border:2px solid #111827;        /* mørk ramme */
  border-radius:10px;
  padding:8px 12px;
  background:#f8fafc;
  text-align:right;
}
.service-ref-label{
  font-size:11px;
  color:#374151;
  font-weight:700;
  letter-spacing:.02em;
  text-transform:uppercase;
}
.service-ref-value{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size:20px;                  /* 👈 dette gjør den “print-tydelig” */
  font-weight:800;
  line-height:1.1;
  color:#111827;
}
.betal-qr{
  display:flex;
  flex-direction:column;
  align-items:center;
  width:max-content;
  margin:6px 0 0 auto;
}
.betal-qr img{
  width:26mm;
  height:26mm;
  image-rendering:pixelated;
}
.betal-qr-label{
  font-size:10px;
  color:#374151;
}
@media print{
  .service-ref-box{
    border-width:2px;
    padding:10px 14px;
  }
  .service-ref-value{
    font-size:22px;                /* litt ekstra for papir */
  }
}


/* Pristilbud-printregler beholdes i komponenten for konsistens om begge kan skrives ut i samme økt */
@media print {
  .printing-quote body > *:not(.app-screen) { display: none !important; }
  .printing-quote .app-screen > *:not(#quote-print-root) { display: none !important; }
  nav, #stickyHeader { display: none !important; }
  .printing-quote .app-screen { background: transparent !important; box-shadow:none!important; border:0!important; border-radius:0!important; padding:0!important; margin:0!important; }
  .printing-quote #quote-print-root { display:block!important; position:static!important; inset:auto!important; margin:0!important; padding:0!important; background:transparent!important; z-index:auto!important; }
  .printing-quote #quote-print-root > div { position:static!important; box-shadow:none!important; background:transparent!important; max-width:none!important; max-height:none!important; overflow:visible!important; padding:0!important; margin:0!important; }
  #quote-paper { width:210mm!important; margin:0 auto!important; padding:0!important; box-sizing:border-box!important; border:none!important; border-radius:0!important; box-shadow:none!important; outline:none!important; }
  .tbl { page-break-inside: avoid; }
  html, body { margin:0!important; padding:0!important; }
}
#quote-print-area { width:100%; min-height:297mm; box-sizing:border-box; font-size:12px; }
`;

